import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import WagersIntroSlider from "../components/WagersIntroSlider";
import { apiUrl } from "../lib/apiBase";

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("ga_token") ||
    ""
  );
}

async function apiGet(path) {
  const token = getToken();

  const res = await fetch(apiUrl(path), {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (res.status === 401) {
    const err = new Error("unauthorized");
    err.code = 401;
    throw err;
  }

  if (res.status === 403) {
    let body = null;
    try {
      body = await res.json();
    } catch {}
    const err = new Error(body?.error || "forbidden");
    err.code = 403;
    err.body = body;
    throw err;
  }

  if (!res.ok) {
    let body = null;
    try {
      body = await res.json();
    } catch {}
    const err = new Error(body?.error || `http_${res.status}`);
    err.code = res.status;
    err.body = body;
    throw err;
  }

  return res.json();
}

function moneyLabel(amountCents) {
  if (typeof amountCents !== "number") return "Custom amount";
  return `$${(amountCents / 100).toFixed(2)}`;
}

function itemId(w) {
  return w.id || w._id;
}

function asItems(res) {
  return Array.isArray(res?.items)
    ? res.items
    : Array.isArray(res)
    ? res
    : [];
}

function lower(v) {
  return String(v || "").trim().toLowerCase();
}

function statusTone(status) {
  const s = lower(status);
  if (s === "completed") return styles.statusCompleted;
  if (s === "disputed") return styles.statusDisputed;
  if (s === "voided") return styles.statusVoided;
  return styles.statusNeutral;
}

function statusLabel(status) {
  const s = lower(status);
  if (!s) return "Unknown";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function escrowLabel(w) {
  const state = lower(w?.escrow?.state);
  if (state === "funded") return "Escrow Funded";
  if (state === "awaiting_opponent") return "Awaiting Opponent Deposit";
  if (state === "awaiting_creator") return "Awaiting Creator Deposit";
  return "Escrow Pending";
}

function refundLabel(w) {
  const status = lower(w?.escrow?.refundStatus);
  if (status === "refunded") return "Refunded";
  if (status === "partial") return "Partial Refund";
  if (status === "pending") return "Refund Pending";
  return "";
}

function resultLine(w, myEmail) {
  const s = lower(w?.status);
  const me = lower(myEmail);
  const winnerEmail = lower(w?.winner?.email);
  const loserEmail = lower(w?.loser?.email);
  const winnerName =
    w?.winner?.username || w?.winner?.tag || w?.winner?.email || "Winner";
  const loserName =
    w?.loser?.username || w?.loser?.tag || w?.loser?.email || "Loser";

  if (s === "completed") {
    if (winnerEmail && me && winnerEmail === me) {
      return `You won vs ${loserName}`;
    }
    if (loserEmail && me && loserEmail === me) {
      return `You lost vs ${winnerName}`;
    }
    return `${winnerName} defeated ${loserName}`;
  }

  if (s === "disputed") return "Room disputed — pending review";
  if (s === "voided") {
    const refund = refundLabel(w);
    return refund
      ? `Room expired before final result — ${refund}`
      : "Room expired before final result";
  }
  return statusLabel(w?.status);
}

function completedAtLabel(w) {
  const ts =
    Number(w?.result?.completedAt || 0) ||
    Number(w?.result?.disputedAt || 0) ||
    Number(w?.timers?.voidedAt || 0) ||
    Number(w?.updatedAt || 0) ||
    Number(w?.createdAt || 0);

  return ts ? new Date(ts).toLocaleString() : "—";
}

export default function Wagers() {
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState([]);
  const [active, setActive] = useState([]);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyFilter, setHistoryFilter] = useState("all");
  const [error, setError] = useState("");
  const [memberRequired, setMemberRequired] = useState(false);

  const token = useMemo(() => getToken(), []);
  const myEmail = useMemo(() => localStorage.getItem("member_email") || "", []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [invitedRes, activeRes, completedRes, disputedRes, voidedRes] =
        await Promise.all([
          apiGet("wagers?status=invited"),
          apiGet("wagers?status=active"),
          apiGet("wagers?status=completed"),
          apiGet("wagers?status=disputed"),
          apiGet("wagers?status=voided"),
        ]);

      const invitedItems = asItems(invitedRes);
      const activeItems = asItems(activeRes);
      const completedItems = asItems(completedRes);
      const disputedItems = asItems(disputedRes);
      const voidedItems = asItems(voidedRes);

      const history = [...completedItems, ...disputedItems, ...voidedItems].sort(
        (a, b) => {
          const aTs =
            Number(a?.result?.completedAt || 0) ||
            Number(a?.result?.disputedAt || 0) ||
            Number(a?.timers?.voidedAt || 0) ||
            Number(a?.updatedAt || 0) ||
            Number(a?.createdAt || 0);
          const bTs =
            Number(b?.result?.completedAt || 0) ||
            Number(b?.result?.disputedAt || 0) ||
            Number(b?.timers?.voidedAt || 0) ||
            Number(b?.updatedAt || 0) ||
            Number(b?.createdAt || 0);
          return bTs - aTs;
        }
      );

      setMemberRequired(
        !!invitedRes?.memberRequired ||
          !!activeRes?.memberRequired ||
          !!completedRes?.memberRequired ||
          !!disputedRes?.memberRequired ||
          !!voidedRes?.memberRequired
      );
      setInvites(invitedItems);
      setActive(activeItems);
      setHistoryItems(history);
    } catch (e) {
      if (e?.code === 401) {
        setError("Please sign in to view wagers.");
      } else if (e?.code === 403) {
        setMemberRequired(true);
        setInvites([]);
        setActive([]);
        setHistoryItems([]);
      } else {
        setError("Wagers are loading. Refresh in a moment if needed.");
        console.error("[Wagers] load error:", e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError("Please sign in to view wagers.");
      return;
    }
    void loadAll();
  }, [loadAll, token]);

  const filteredHistory = useMemo(() => {
    if (historyFilter === "all") return historyItems;
    return historyItems.filter((w) => lower(w?.status) === historyFilter);
  }, [historyFilter, historyItems]);

  return (
    <div className="page">
      <div style={{ marginBottom: 18 }}>
        <WagersIntroSlider />
      </div>

      <section style={styles.shellCard}>
        <div style={styles.headerRow}>
          <div>
            <div style={styles.kicker}>Wagers Hub</div>
            <h2 style={styles.title}>Create your own custom wager room</h2>
            <p style={styles.subtitle}>
              Pick any game, any mode, set your rules, invite an opponent, and let
              GAStreaming act as the middleman while both sides confirm the result.
            </p>
          </div>

          <div style={styles.actions}>
            <Link
              style={{ ...styles.ctaBtn, ...styles.ctaPrimary }}
              to="/wagers/create"
            >
              Create a Wager
            </Link>

            <button
              type="button"
              style={styles.ctaBtn}
              onClick={() => setHistoryOpen(true)}
            >
              History {historyItems.length ? `(${historyItems.length})` : ""}
            </button>

            <Link style={styles.ctaBtn} to="/wagers/terms">
              Read Terms
            </Link>

            <button
              type="button"
              style={styles.ctaBtn}
              onClick={loadAll}
              disabled={loading}
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        <div style={styles.infoGrid}>
          <div style={styles.infoCard}>
            <div style={styles.infoLabel}>Account email</div>
            <div style={styles.infoValue}>{myEmail || "—"}</div>
            <div style={styles.infoHelp}>
              This is the member email tied to your wager access.
            </div>
          </div>

          <div style={styles.infoCard}>
            <div style={styles.infoLabel}>Who can wager</div>
            <div style={styles.infoValue}>
              {memberRequired ? "Paid members only" : "Active members"}
            </div>
            <div style={styles.infoHelp}>
              Both players must be active members before a wager can move forward.
            </div>
          </div>

          <div style={styles.infoCard}>
            <div style={styles.infoLabel}>How it works</div>
            <div style={styles.infoValue}>Create → fund → play → confirm</div>
            <div style={styles.infoHelp}>
              Players create a private wager room, both deposits fund escrow, then they play and report the result.
            </div>
          </div>
        </div>

        {memberRequired ? (
          <div style={styles.noticeBox}>
            Wagers require an active <strong>Get Active Membership</strong>.{" "}
            <Link to="/membership" style={styles.inlineLink}>
              Become a Member
            </Link>
          </div>
        ) : null}

        {error ? <div style={styles.noticeBox}>{error}</div> : null}

        <div style={styles.columns}>
          <div style={styles.columnCard}>
            <div style={styles.columnHeader}>
              <div>
                <div style={styles.columnTitle}>Invites for You</div>
                <div style={styles.columnSub}>
                  Pending match requests waiting on your response.
                </div>
              </div>
              <div style={styles.countPill}>{invites.length}</div>
            </div>

            {loading ? (
              <div style={styles.emptyState}>Loading invites...</div>
            ) : invites.length === 0 ? (
              <div style={styles.emptyState}>
                No invites right now. When another member creates a wager for you,
                it will show up here.
              </div>
            ) : (
              <div style={styles.list}>
                {invites.map((w) => (
                  <div key={itemId(w)} style={styles.itemCard}>
                    <div style={styles.itemTop}>
                      <div>
                        <div style={styles.itemTitle}>
                          {w?.match?.game || "Any game"} •{" "}
                          {w?.match?.mode || "Custom mode"}
                        </div>
                        <div style={styles.itemMeta}>
                          Invite from{" "}
                          {w?.creator?.username ||
                            w?.creator?.email ||
                            "Unknown Creator"}{" "}
                          • {w?.match?.platform || "Any platform"}
                        </div>
                      </div>
                      <div style={styles.amountBadge}>
                        {moneyLabel(w?.amountCents)}
                      </div>
                    </div>

                    {w?.match?.rules ? (
                      <div style={styles.ruleText}>
                        <strong>Rules:</strong> {w.match.rules}
                      </div>
                    ) : null}

                    <div style={styles.itemActions}>
                      <Link style={styles.smallBtn} to={`/wagers/${itemId(w)}`}>
                        Open Room
                      </Link>
                      <Link
                        style={{
                          ...styles.smallBtn,
                          ...styles.smallBtnPrimary,
                        }}
                        to={`/wagers/${itemId(w)}?action=accept`}
                      >
                        Accept Invite
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.columnCard}>
            <div style={styles.columnHeader}>
              <div>
                <div style={styles.columnTitle}>Open / Active Wagers</div>
                <div style={styles.columnSub}>
                  Rooms you can reopen, manage, or report results from.
                </div>
              </div>
              <div style={styles.countPill}>{active.length}</div>
            </div>

            {loading ? (
              <div style={styles.emptyState}>Loading active wagers...</div>
            ) : active.length === 0 ? (
              <div style={styles.emptyState}>
                No open wagers right now. Create a room to start a custom
                member-vs-member wager.
              </div>
            ) : (
              <div style={styles.list}>
                {active.map((w) => {
                  const id = itemId(w);
                  const creatorEmail = lower(w?.creator?.email);
                  const youAreCreator =
                    !!myEmail && creatorEmail === lower(myEmail);
                  const opponentLabel = youAreCreator
                    ? w?.opponent?.username ||
                      w?.opponent?.email ||
                      "Opponent"
                    : w?.creator?.username || w?.creator?.email || "Creator";

                  return (
                    <div key={id} style={styles.itemCard}>
                      <div style={styles.itemTop}>
                        <div>
                          <div style={styles.itemTitle}>
                            {w?.match?.game || "Any game"} •{" "}
                            {w?.match?.mode || "Custom mode"}
                          </div>
                          <div style={styles.itemMeta}>
                            You vs {opponentLabel} •{" "}
                            {w?.match?.platform || "Any platform"}
                          </div>
                        </div>
                        <div style={styles.amountBadge}>
                          {moneyLabel(w?.amountCents)}
                        </div>
                      </div>

                      {w?.timers?.expiresAt ? (
                        <div style={styles.ruleText}>
                          <strong>Expires:</strong>{" "}
                          {new Date(
                            Number(w.timers.expiresAt)
                          ).toLocaleString()}
                        </div>
                      ) : null}

                      <div style={styles.ruleText}>
                        <strong>Escrow:</strong> {escrowLabel(w)}
                      </div>

                      {!!refundLabel(w) && (
                        <div style={styles.ruleText}>
                          <strong>Refund:</strong> {refundLabel(w)}
                        </div>
                      )}

                      <div style={styles.itemActions}>
                        <Link style={styles.smallBtn} to={`/wagers/${id}`}>
                          Open Room
                        </Link>
                        <Link
                          style={{
                            ...styles.smallBtn,
                            ...styles.smallBtnPrimary,
                          }}
                          to={`/wagers/${id}`}
                        >
                          {lower(w?.escrow?.state) === "funded" ? "Ready To Play" : "Pay / Check Escrow"}
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {historyOpen && (
        <div
          style={styles.modalOverlay}
          onClick={() => setHistoryOpen(false)}
        >
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.kicker}>Past Wagers</div>
                <h3 style={styles.modalTitle}>Wager History</h3>
                <div style={styles.modalSub}>
                  Completed, disputed, and voided rooms you can review anytime.
                </div>
              </div>

              <button
                type="button"
                style={styles.closeBtn}
                onClick={() => setHistoryOpen(false)}
              >
                Close
              </button>
            </div>

            <div style={styles.filterRow}>
              {[
                ["all", "All"],
                ["completed", "Wins / Losses"],
                ["disputed", "Disputed"],
                ["voided", "Voided"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setHistoryFilter(value)}
                  style={{
                    ...styles.filterBtn,
                    ...(historyFilter === value ? styles.filterBtnActive : {}),
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            <div style={styles.historyList}>
              {filteredHistory.length === 0 ? (
                <div style={styles.emptyState}>
                  No past wagers in this category yet.
                </div>
              ) : (
                filteredHistory.map((w) => {
                  const id = itemId(w);
                  const creatorEmail = lower(w?.creator?.email);
                  const youAreCreator =
                    !!myEmail && creatorEmail === lower(myEmail);
                  const opponentLabel = youAreCreator
                    ? w?.opponent?.username ||
                      w?.opponent?.tag ||
                      w?.opponent?.email ||
                      "Opponent"
                    : w?.creator?.username ||
                      w?.creator?.tag ||
                      w?.creator?.email ||
                      "Creator";

                  return (
                    <div key={id} style={styles.historyCard}>
                      <div style={styles.historyTop}>
                        <div>
                          <div style={styles.itemTitle}>
                            {w?.match?.game || "Any game"} •{" "}
                            {w?.match?.mode || "Custom mode"}
                          </div>
                          <div style={styles.itemMeta}>
                            vs {opponentLabel} •{" "}
                            {w?.match?.platform || "Any platform"}
                          </div>
                        </div>

                        <div style={styles.historyRight}>
                          <div style={styles.amountBadge}>
                            {moneyLabel(w?.amountCents)}
                          </div>
                          <div
                            style={{
                              ...styles.statusBadge,
                              ...statusTone(w?.status),
                            }}
                          >
                            {statusLabel(w?.status)}
                          </div>
                        </div>
                      </div>

                      <div style={styles.historyResult}>
                        {resultLine(w, myEmail)}
                      </div>

                      {!!refundLabel(w) && (
                        <div style={styles.historyDate}>
                          Refund: {refundLabel(w)}
                        </div>
                      )}

                      <div style={styles.historyDate}>
                        Updated: {completedAtLabel(w)}
                      </div>

                      <div style={styles.itemActions}>
                        <Link
                          style={styles.smallBtn}
                          to={`/wagers/${id}`}
                          onClick={() => setHistoryOpen(false)}
                        >
                          Open Room
                        </Link>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  shellCard: {
    borderRadius: 22,
    padding: 20,
    background: "rgba(15, 10, 14, 0.78)",
    border: "1px solid rgba(255, 30, 30, 0.18)",
    boxShadow: "0 22px 80px rgba(0,0,0,0.55)",
    backdropFilter: "blur(10px)",
    marginBottom: 18,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 18,
    flexWrap: "wrap",
  },
  kicker: {
    color: "rgba(255,255,255,0.74)",
    textTransform: "uppercase",
    letterSpacing: 1.4,
    fontSize: 12,
    marginBottom: 10,
  },
  title: {
    margin: 0,
    color: "#fff",
    fontSize: 34,
    lineHeight: 1.05,
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  subtitle: {
    margin: "10px 0 0",
    color: "rgba(235,240,255,0.82)",
    lineHeight: 1.55,
    maxWidth: 860,
  },
  actions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
  },
  ctaBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "13px 18px",
    borderRadius: 14,
    textDecoration: "none",
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(0,0,0,0.28)",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
  ctaPrimary: {
    background: "linear-gradient(180deg, #ff2020 0%, #e10000 100%)",
    border: "1px solid rgba(255,30,30,0.28)",
    boxShadow: "0 14px 30px rgba(200,0,0,0.22)",
  },
  infoGrid: {
    marginTop: 18,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  infoCard: {
    borderRadius: 16,
    padding: 16,
    background: "rgba(0,0,0,0.28)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  infoLabel: {
    color: "rgba(255,255,255,0.70)",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 8,
  },
  infoValue: {
    color: "#fff",
    fontSize: 20,
    fontWeight: 800,
    marginBottom: 6,
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  infoHelp: {
    color: "rgba(235,240,255,0.78)",
    lineHeight: 1.45,
    fontSize: 14,
  },
  noticeBox: {
    marginTop: 16,
    borderRadius: 14,
    padding: "12px 14px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(235,240,255,0.94)",
  },
  inlineLink: {
    color: "#fff",
    textDecoration: "underline",
    fontWeight: 700,
  },
  columns: {
    marginTop: 18,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 16,
  },
  columnCard: {
    borderRadius: 18,
    padding: 18,
    background: "rgba(0,0,0,0.34)",
    border: "1px solid rgba(255,255,255,0.10)",
    minHeight: 360,
  },
  columnHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 14,
  },
  columnTitle: {
    color: "#fff",
    fontSize: 24,
    fontWeight: 800,
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  columnSub: {
    color: "rgba(235,240,255,0.78)",
    lineHeight: 1.45,
    marginTop: 6,
    fontSize: 14,
  },
  countPill: {
    minWidth: 36,
    height: 36,
    borderRadius: 999,
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontWeight: 800,
    background: "rgba(255,0,0,0.18)",
    border: "1px solid rgba(255,0,0,0.20)",
  },
  emptyState: {
    borderRadius: 16,
    padding: 18,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(235,240,255,0.82)",
    lineHeight: 1.55,
  },
  list: {
    display: "grid",
    gap: 12,
  },
  itemCard: {
    borderRadius: 16,
    padding: 16,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  itemTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
  },
  itemTitle: {
    color: "#fff",
    fontWeight: 800,
    fontSize: 17,
  },
  itemMeta: {
    color: "rgba(235,240,255,0.78)",
    marginTop: 6,
    lineHeight: 1.45,
  },
  amountBadge: {
    padding: "8px 12px",
    borderRadius: 999,
    color: "#fff",
    fontWeight: 800,
    background: "rgba(255,0,0,0.16)",
    border: "1px solid rgba(255,0,0,0.22)",
    whiteSpace: "nowrap",
  },
  ruleText: {
    marginTop: 10,
    color: "rgba(235,240,255,0.86)",
    lineHeight: 1.5,
  },
  itemActions: {
    marginTop: 14,
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  smallBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 42,
    padding: "10px 14px",
    borderRadius: 12,
    textDecoration: "none",
    color: "#fff",
    fontWeight: 700,
    background: "rgba(0,0,0,0.28)",
    border: "1px solid rgba(255,255,255,0.10)",
    cursor: "pointer",
  },
  smallBtnPrimary: {
    background: "linear-gradient(180deg, #ff2020 0%, #e10000 100%)",
    border: "1px solid rgba(255,30,30,0.28)",
    boxShadow: "0 10px 24px rgba(200,0,0,0.22)",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.68)",
    backdropFilter: "blur(4px)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
  },
  modalCard: {
    width: "min(980px, 100%)",
    maxHeight: "90vh",
    overflow: "auto",
    borderRadius: 22,
    padding: 20,
    background: "rgba(15, 10, 14, 0.96)",
    border: "1px solid rgba(255, 30, 30, 0.18)",
    boxShadow: "0 22px 80px rgba(0,0,0,0.60)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
  },
  modalTitle: {
    margin: 0,
    color: "#fff",
    fontSize: 30,
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  modalSub: {
    color: "rgba(235,240,255,0.78)",
    marginTop: 8,
    lineHeight: 1.45,
  },
  closeBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 42,
    padding: "10px 14px",
    borderRadius: 12,
    color: "#fff",
    fontWeight: 700,
    background: "rgba(0,0,0,0.28)",
    border: "1px solid rgba(255,255,255,0.10)",
    cursor: "pointer",
  },
  filterRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 18,
    marginBottom: 18,
  },
  filterBtn: {
    padding: "10px 14px",
    borderRadius: 999,
    color: "#fff",
    fontWeight: 700,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.10)",
    cursor: "pointer",
  },
  filterBtnActive: {
    background: "linear-gradient(180deg, #ff2020 0%, #e10000 100%)",
    border: "1px solid rgba(255,30,30,0.28)",
  },
  historyList: {
    display: "grid",
    gap: 12,
  },
  historyCard: {
    borderRadius: 16,
    padding: 16,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  historyTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
  },
  historyRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  statusBadge: {
    padding: "8px 12px",
    borderRadius: 999,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  statusCompleted: {
    background: "rgba(58, 181, 74, 0.16)",
    border: "1px solid rgba(58, 181, 74, 0.24)",
    color: "#d9ffe0",
  },
  statusDisputed: {
    background: "rgba(255, 172, 64, 0.16)",
    border: "1px solid rgba(255, 172, 64, 0.24)",
    color: "#ffe7c4",
  },
  statusVoided: {
    background: "rgba(180, 180, 180, 0.16)",
    border: "1px solid rgba(180, 180, 180, 0.24)",
    color: "#f0f0f0",
  },
  statusNeutral: {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "#fff",
  },
  historyResult: {
    marginTop: 12,
    color: "#fff",
    fontWeight: 700,
  },
  historyDate: {
    marginTop: 8,
    color: "rgba(235,240,255,0.72)",
    fontSize: 14,
  },
};